import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { mfaService } from '../../../services/mfaService';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const MfaVerificationStep = ({ factorId, onVerified, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  useEffect(() => {
    if (factorId && !useBackupCode) {
      initChallenge();
    }
  }, [factorId, useBackupCode]);

  const initChallenge = async () => {
    const { data, error: challengeError } = await mfaService?.challengeMfa(factorId);
    if (challengeError) {
      setError(challengeError?.message || 'Failed to initialize MFA challenge');
      return;
    }
    setChallengeId(data?.id);
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    setError('');
    setIsLoading(true);

    if (!verificationCode) {
      setError('Please enter a verification code');
      setIsLoading(false);
      return;
    }

    if (useBackupCode) {
      // Verify backup code
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        setError('User session not found');
        setIsLoading(false);
        return;
      }

      const { data: isValid } = await mfaService?.verifyBackupCode(user?.id, verificationCode);
      
      if (isValid) {
        onVerified?.();
      } else {
        setError('Invalid or already used backup code');
      }
      setIsLoading(false);
      return;
    }

    // Verify TOTP code
    const { error: verifyError } = await mfaService?.verifyMfaCode(
      factorId,
      challengeId,
      verificationCode
    );

    if (verifyError) {
      setError(verifyError?.message || 'Invalid verification code');
      setIsLoading(false);
      return;
    }

    onVerified?.();
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
          <Icon name="Shield" size={32} color="var(--color-primary)" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Two-Factor Authentication
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          {useBackupCode
            ? 'Enter one of your backup codes' :'Enter the 6-digit code from your authenticator app'}
        </p>
      </div>

      {error && (
        <div className="p-3 bg-error/10 border border-error rounded-md flex items-start space-x-2">
          <Icon name="AlertCircle" size={18} color="var(--color-error)" />
          <p className="font-body text-sm text-error flex-1">{error}</p>
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-4">
        <Input
          type="text"
          label={useBackupCode ? 'Backup Code' : 'Verification Code'}
          placeholder={useBackupCode ? 'XXXXXXXX' : '000000'}
          value={verificationCode}
          onChange={(e) => {
            const value = useBackupCode
              ? e?.target?.value?.toUpperCase()?.slice(0, 8)
              : e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
            setVerificationCode(value);
            setError('');
          }}
          maxLength={useBackupCode ? 8 : 6}
          required
        />

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={isLoading || !verificationCode}
          iconName="Check"
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </Button>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => {
              setUseBackupCode(!useBackupCode);
              setVerificationCode('');
              setError('');
            }}
            className="font-body text-sm text-primary hover:underline"
          >
            {useBackupCode ? 'Use authenticator code' : 'Use backup code'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="font-body text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MfaVerificationStep;